import { useStore } from 'app/context';
import { UserModel } from 'app/models/core/user.model';
import { BnrActionCountRow } from 'app/stores/admin.log.store';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { ColorInfoBox, PairedBars, YearLegend, isRelevantUser } from './UsageAnmeldequoteShared';
import { formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import colors from 'assets/scss/colors.module.scss';

interface VdViewRow {
    vd: string;
    activeCount: number;
    viewCount: number;
    prevViewCount: number;
    avg: number;
    prevAvg: number;
}

const buildRows = (users: UserModel[], currCounts: BnrActionCountRow[], prevCounts: BnrActionCountRow[]): VdViewRow[] => {
    const currByBnr = new Map(currCounts.map((c) => [c.bnr_id, c.action_count]));
    const prevByBnr = new Map(prevCounts.map((c) => [c.bnr_id, c.action_count]));

    const byVd = new Map<string, VdViewRow>();
    users.forEach((u) => {
        if (!isRelevantUser(u)) {
            return;
        }
        const vd = u.firstVd;
        if (!vd) {
            return;
        }
        let row = byVd.get(vd);
        if (!row) {
            row = {
                vd,
                activeCount: 0,
                viewCount: 0,
                prevViewCount: 0,
                avg: 0,
                prevAvg: 0,
            };
            byVd.set(vd, row);
        }
        row.activeCount++;
        row.viewCount += currByBnr.get(u.bnrId) || 0;
        row.prevViewCount += prevByBnr.get(u.bnrId) || 0;
    });

    const rows = Array.from(byVd.values());
    rows.forEach((r) => {
        r.avg = r.activeCount > 0 ? Math.round((r.viewCount / r.activeCount) * 10) / 10 : 0;
        r.prevAvg = r.activeCount > 0 ? Math.round((r.prevViewCount / r.activeCount) * 10) / 10 : 0;
    });
    return rows.sort((a, b) => a.vd.localeCompare(b.vd));
};

export const UsageViewVdYtd = observer(() => {
    const { adminLogStore, adminUserStore } = useStore();
    const [rows, setRows] = useState<VdViewRow[]>([]);
    const [loading, setLoading] = useState(true);

    const now = moment();
    const currYear = now.year();
    const prevYear = currYear - 1;

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            // Vorjahreswert über den gleichen Zeitraum (1.1. bis heute), damit YTD vergleichbar ist
            const [users, currCounts, prevCounts] = await Promise.all([
                adminUserStore.findAllActiveUsers(),
                adminLogStore.loadActionCountsByBnrId('nav', `${currYear}-01-01`, moment().add(1, 'day').format('YYYY-MM-DD')),
                adminLogStore.loadActionCountsByBnrId('nav', `${prevYear}-01-01`, moment().subtract(1, 'year').add(1, 'day').format('YYYY-MM-DD')),
            ]);
            setRows(buildRows(users, currCounts, prevCounts));
            setLoading(false);
        };
        load();
    }, [adminLogStore, adminUserStore, currYear, prevYear]);

    if (loading) {
        return <div>Wird geladen...</div>;
    }

    const maxAvg = rows.reduce((max, r) => Math.max(max, r.avg, r.prevAvg), 0);

    const tm = new TableModel<VdViewRow, string>();
    const cols: ITableColumn<VdViewRow, string>[] = [
        {
            label: 'VD',
            path: 'vd',
        },
        {
            label: 'Aktive Benutzer',
            path: 'activeCount',
            format: formatNum,
            agg: [{ aggType: AggregrationType.sum }],
        },
        {
            label: 'Views',
            path: 'viewCount',
            format: formatNum,
            agg: [{ aggType: AggregrationType.sum }],
        },
        {
            label: 'Ø Views YTD',
            path: 'avg',
            render: (row: TableRow<VdViewRow>) => (
                <PairedBars curr={row.data.avg} prev={row.data.prevAvg} max={maxAvg} formatValue={formatNum} />
            ),
        },
    ];
    tm.setCols(cols);
    tm.sortBy = 'vd';
    tm.sortAsc = true;
    tm.idProperty = 'vd';
    tm.setRowData(rows);

    const totalActive = rows.reduce((sum, r) => sum + r.activeCount, 0);
    const totalViews = rows.reduce((sum, r) => sum + r.viewCount, 0);
    const totalPrevViews = rows.reduce((sum, r) => sum + r.prevViewCount, 0);
    const totalAvg = totalActive > 0 ? Math.round((totalViews / totalActive) * 10) / 10 : 0;
    const totalPrevAvg = totalActive > 0 ? Math.round((totalPrevViews / totalActive) * 10) / 10 : 0;

    return (
        <>
            <ColorInfoBox
                currYear={currYear}
                prevYear={prevYear}
                currText={`Ø Views ${currYear} — durchschnittliche Anzahl Seitenaufrufe (Navigations-Tracking) je aktivem Benutzer seit dem 1.1.${currYear}. Achtung: Navigation wird erst seit dem 19.3.2026 getrackt, die Daten sind daher unvollständig.`}
                prevText={`Vorjahreswert ${prevYear} — gleicher Zeitraum (1.1. bis heute) im Vorjahr, bezogen auf die heutigen aktiven Benutzer. Vor dem 19.3.2026 liegen keine Navigationsdaten vor.`}
            />
            <Table tm={tm} stickyFooter={true} />
            <YearLegend currYear={currYear} prevYear={prevYear} />
            <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem', marginTop: '0.5rem', color: colors.grey }}>
                <span>Aktive Benutzer gesamt: {formatNum(totalActive)}</span>
                <span>
                    Views {currYear}: {formatNum(totalViews)} (Ø {totalAvg})
                </span>
                <span>
                    Views {prevYear}: {formatNum(totalPrevViews)} (Ø {totalPrevAvg})
                </span>
            </div>
        </>
    );
});
